// deps
import React, { memo, useCallback } from "react";

// internal-deps
import { ElementObserver } from "@swiggy-private/core-ui";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import { getCloudinaryUrl, TEAM_LINE } from "@swiggy-private/js-utils";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// types
import { CTA_TYPE, IVoucherEntityCardData } from "@dineout-payment-success-ui/interfaces";

// styles
import {
    ENTITY_IMAGE_SIZE,
    EntityChevronWrapper,
    EntityImage,
    EntityMetaSeparator,
    EntityMetaWrapper,
    EntitySubtitle,
    EntityTextWrapper,
    RATING_ICON_SIZE,
    RatingWrapper,
    VoucherEntityCardWrapper,
} from "./styles";
import { VOUCHER_ENTITY_CARD_TEST_IDS } from "./testIds";

const STORE_RATING_ICON = "portal/static-assets/icons/dineout/store_rating.png";
const STORE_RATING_ICON_URL = getCloudinaryUrl(STORE_RATING_ICON, {}, { teamLine: TEAM_LINE.PORTAL });

interface IVoucherEntityCardProps {
    voucherEntityCardData?: IVoucherEntityCardData;
    onClick?: (link?: string, type?: CTA_TYPE) => void;
    onLoadImpressionEvent?: (voucherEntityCardData?: IVoucherEntityCardData) => void;
}

const VoucherEntityCard = ({ voucherEntityCardData, onClick, onLoadImpressionEvent }: IVoucherEntityCardProps) => {
    const { imageUrl, title, subtitle, rating, ctaLink, ctaType } = voucherEntityCardData || {};

    const handleView = useCallback(() => {
        onLoadImpressionEvent?.(voucherEntityCardData);
    }, [onLoadImpressionEvent, voucherEntityCardData]);

    const handleClick = useCallback(() => {
        onClick?.(ctaLink, ctaType);
    }, [ctaLink, ctaType, onClick]);

    if (!title || !imageUrl) {
        return null;
    }

    return (
        <ElementObserver onViewEnter={handleView}>
            <VoucherEntityCardWrapper
                type="button"
                onClick={handleClick}
                data-testid={VOUCHER_ENTITY_CARD_TEST_IDS.CARD}
            >
                <EntityImage
                    cloudinaryId={imageUrl}
                    alt={title}
                    width={ENTITY_IMAGE_SIZE}
                    height={ENTITY_IMAGE_SIZE}
                    data-testid={VOUCHER_ENTITY_CARD_TEST_IDS.IMAGE}
                />
                <EntityTextWrapper>
                    <Text
                        variant="V2.body1.neutral.medium"
                        color="text_Highest_Emphasis"
                        data-testid={VOUCHER_ENTITY_CARD_TEST_IDS.TITLE}
                    >
                        {title}
                    </Text>
                    {(rating || subtitle) && (
                        <EntityMetaWrapper data-testid={VOUCHER_ENTITY_CARD_TEST_IDS.META}>
                            {rating && (
                                <RatingWrapper data-testid={VOUCHER_ENTITY_CARD_TEST_IDS.RATING}>
                                    <Image
                                        src={STORE_RATING_ICON_URL}
                                        alt=""
                                        width={RATING_ICON_SIZE}
                                        height={RATING_ICON_SIZE}
                                        data-testid={VOUCHER_ENTITY_CARD_TEST_IDS.RATING_ICON}
                                    />
                                    <Text variant="V2.overline.neutral.bold" color="text_Highest_Emphasis">
                                        {rating}
                                    </Text>
                                </RatingWrapper>
                            )}
                            {rating && subtitle && (
                                <EntityMetaSeparator data-testid={VOUCHER_ENTITY_CARD_TEST_IDS.META_SEPARATOR}>
                                    <SpriteIconWeb iconName="bullet12" fillColor="border_Subtle_Secondary" />
                                </EntityMetaSeparator>
                            )}
                            {subtitle && (
                                <EntitySubtitle
                                    variant="V2.overline.neutral.medium"
                                    color="text_Med_Emphasis"
                                    data-testid={VOUCHER_ENTITY_CARD_TEST_IDS.SUBTITLE}
                                >
                                    {subtitle}
                                </EntitySubtitle>
                            )}
                        </EntityMetaWrapper>
                    )}
                </EntityTextWrapper>
                {ctaLink && (
                    <EntityChevronWrapper data-testid={VOUCHER_ENTITY_CARD_TEST_IDS.CHEVRON}>
                        <SpriteIconWeb iconName="chevronRight12" fillColor="text_High_Emphasis" />
                    </EntityChevronWrapper>
                )}
            </VoucherEntityCardWrapper>
        </ElementObserver>
    );
};

export default memo(VoucherEntityCard);